/** @file unfilterScanlines: reverses PNG filters 0-4 on the bytes-per-pixel stride. */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { IMAGE_IO_ERRORS } from '../errors/imageIoErrors';
import { channelsOfColorType } from './channelsOfColorType';
import type { PngHeader } from './pngHeader';

/** The Paeth predictor of PNG §9.4 (ties prefer left, then up). */
function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  return pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
}

/** Predictor per filter type from left `a`, up `b` and upper-left `c` (PNG §9.2). */
const PREDICT: readonly ((a: number, b: number, c: number) => number)[] = [
  () => 0, (a) => a, (_a, b) => b, (a, b) => (a + b) >> 1, paeth,
];

/** Unfilters one row in place: `cur` holds filtered bytes, `prev` the previous unfiltered row (zeros for the first). */
function unfilterRow(f: number, cur: Uint8Array, prev: Uint8Array, bpp: number): boolean {
  const predict = PREDICT[f];
  if (predict === undefined) return false;
  if (f === 0) return true;
  for (let i = 0; i < cur.length; i++) {
    const a = i >= bpp ? cur[i - bpp] ?? 0 : 0;
    const c = i >= bpp ? prev[i - bpp] ?? 0 : 0;
    cur[i] = ((cur[i] ?? 0) + predict(a, prev[i] ?? 0, c)) & 0xff;
  }
  return true;
}

/** Unfiltered rows (`height * rowBytes`, filter bytes removed); `PNG_TRUNCATED` when short, `PNG_FILTER` on a type > 4. */
export function unfilterScanlines(raw: Uint8Array, header: PngHeader): Uint8Array {
  const bits = header.bitDepth * channelsOfColorType(header.colorType);
  const bpp = Math.max(1, Math.ceil(bits / 8));
  const rowBytes = Math.ceil((header.width * bits) / 8);
  const path = { path: '/png/IDAT' };
  if (raw.length < header.height * (rowBytes + 1)) {
    return throwError(IMAGE_IO_ERRORS.PNG_TRUNCATED, { expected: header.height * (rowBytes + 1), got: raw.length }, path);
  }
  const out = new Uint8Array(header.height * rowBytes);
  let prev = new Uint8Array(rowBytes);
  for (let y = 0; y < header.height; y++) {
    const src = y * (rowBytes + 1);
    const cur = out.subarray(y * rowBytes, (y + 1) * rowBytes);
    cur.set(raw.subarray(src + 1, src + 1 + rowBytes));
    const f = raw[src] ?? 0;
    if (!unfilterRow(f, cur, prev, bpp)) return throwError(IMAGE_IO_ERRORS.PNG_FILTER, { row: y, filter: f }, path);
    prev = cur;
  }
  return out;
}
